import React, { useCallback, useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Info } from 'lucide-react';
import { toast } from 'sonner';
import { brl } from './format';

const fmtDateTime = (iso) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const ORIGEM = { portal: 'portal', painel: 'painel' };
const valueLabel = (v, configured = true) => (!configured ? 'Não configurado' : v == null ? 'Sem frete grátis por valor' : brl(v));

const LimitForm = ({ limit, configured, detected, onSave, onCancel }) => {
  const [v, setV] = useState(limit != null ? String(limit) : '');
  const [noFree, setNoFree] = useState(configured && limit == null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const n = Number(String(v).replace(',', '.'));
    if (!noFree && !(n > 0 && n <= 100000)) { toast.error('Informe um valor maior que R$ 0 e até R$ 100.000.'); return; }
    setSaving(true);
    try { await onSave(noFree ? null : n); toast.success('Limite de frete grátis salvo'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível salvar o limite'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 rounded-lg border p-4" data-testid="free-shipping-limit-form">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Valor mínimo para frete grátis (R$)</label>
          <Input type="number" min="1" max="100000" step="0.01" value={v} disabled={noFree} onChange={(e) => setV(e.target.value)} className="w-40 mt-1" data-testid="free-shipping-limit-input" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="free-shipping-limit-save">Salvar</Button>
        {onCancel && <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="free-shipping-limit-cancel">Cancelar</Button>}
      </div>
      {detected != null && !noFree && (
        <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setV(String(detected))} data-testid="free-shipping-detected">
          Detectado nos pedidos: ~{brl(detected)} (clique para usar)
        </button>
      )}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={noFree} onCheckedChange={(c) => setNoFree(!!c)} data-testid="free-shipping-no-limit-checkbox" />
        Minha loja não tem frete grátis por valor mínimo
      </label>
    </div>
  );
};

const HistoryDialog = ({ open, onOpenChange, rows }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="limit-history-dialog">
      <DialogHeader><DialogTitle>Histórico do limite de frete grátis</DialogTitle></DialogHeader>
      <ul className="divide-y max-h-[60vh] overflow-y-auto text-sm">
        {rows.map((h) => (
          <li key={h.id} className="py-2" data-testid="limit-history-row">
            <p><span className="text-muted-foreground">{valueLabel(h.valor_anterior, h.anterior_configurado)}</span> → <span className="font-medium">{valueLabel(h.valor_novo)}</span></p>
            <p className="text-xs text-muted-foreground">{h.alterado_por} ({ORIGEM[h.origem] || h.origem}) · {fmtDateTime(h.created_at)}</p>
          </li>
        ))}
        {!rows.length && <li className="py-4 text-muted-foreground">Nenhuma alteração registrada.</li>}
      </ul>
    </DialogContent>
  </Dialog>
);

const Audit = ({ api, version }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get('/limit-history').then((r) => setRows(r.data)).catch(() => setRows([])), [api]);
  useEffect(() => { load(); }, [load, version]);
  if (!rows.length) return null;
  const last = rows[0];
  return (
    <p className="text-xs text-muted-foreground" data-testid="limit-last-change">
      Alterado por {last.alterado_por} ({ORIGEM[last.origem] || last.origem}) em {fmtDateTime(last.created_at)} ·{' '}
      <button type="button" className="text-indigo-600 hover:underline" onClick={() => setOpen(true)} data-testid="limit-history-open">Ver histórico</button>
      <HistoryDialog open={open} onOpenChange={setOpen} rows={rows} />
    </p>
  );
};

// Shows/edits frete_gratis_limite. `auditApi` (panel only) enables last change + history.
export const LimitControl = ({ limit, configured, detected, onSave, auditApi }) => {
  const [editing, setEditing] = useState(!configured);
  const [version, setVersion] = useState(0);
  useEffect(() => { if (!configured) setEditing(true); }, [configured]);
  const save = async (v) => { await onSave(v); setEditing(false); setVersion((x) => x + 1); };
  return (
    <div className="space-y-2" data-testid="free-shipping-limit-control">
      {!configured && (
        <Alert data-testid="free-shipping-limit-missing">
          <Info className="h-4 w-4" />
          <AlertDescription>Informe o valor mínimo de frete grátis da sua loja para usar o simulador.</AlertDescription>
        </Alert>
      )}
      {configured && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm" data-testid="free-shipping-limit-current">
            {limit != null
              ? <>Limite atual de frete grátis: <span className="font-semibold">{brl(limit)}</span></>
              : <span className="font-medium">Loja sem frete grátis por valor mínimo</span>}
          </p>
          {!editing && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="free-shipping-limit-edit">Alterar</Button>}
        </div>
      )}
      {editing && <LimitForm limit={limit} configured={configured} detected={detected} onSave={save} onCancel={configured ? () => setEditing(false) : null} />}
      {auditApi && <Audit api={auditApi} version={version} />}
    </div>
  );
};
